/**
 * Login Screen Component
 *
 * This component handles user authentication via email/password login,
 * social authentication providers, and provides options for password reset
 * and email verification. It demonstrates form validation, error handling,
 * and Supabase authentication integration.
 */
import type { Route } from "./+types/login";

import { AlertCircle, Loader2Icon } from "lucide-react";
import { useEffect, useRef, useState, useMemo } from "react";
import { Form, Link, data, redirect, useFetcher, useSearchParams } from "react-router";
import { z } from "zod";



import FormButton from "~/core/components/form-button";
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from "~/core/components/ui/alert";
import { Button } from "~/core/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "~/core/components/ui/card";
import { Input } from "~/core/components/ui/input";
import { setAccessTokenToCookie, clearAuthData } from "~/core/lib/api";

import FormErrors from "../../../core/components/form-error";
import { authApi } from "../api/auth-api";
import { tournamentApi } from "~/features/tournaments/api/tournaments-api";
import { Checkbox } from "~/core/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger } from "~/core/components/ui/select";
import { Label } from "~/core/components/ui/label";

/**
 * Meta function for the login page
 *
 * Sets the page title using the application name from environment variables
 */
export const meta: Route.MetaFunction = () => {
  return [
    {
      title: `Log in | ${import.meta.env.VITE_APP_NAME}`,
    },
  ];
};/**
 * Form validation schema for login
 *
 * Uses Zod to validate:
 * - Email: Must be a valid email format
 * - Password: Must be at least 8 characters long
 *
 * Error messages are provided for user feedback
 */
export const loader = async ({ params, request }: Route.LoaderArgs) => {
  const recentTournaments = await tournamentApi.getRecentTournaments();
  return { recentTournaments };
};

const loginSchema = z.object({
  tnmtId: z.string().min(1, { message: "Tournament ID is required" }),
  password: z
    .string()
    .min(8, { message: "Password must be at least 8 characters long" }),
});
/**
 * Server action for handling login form submission
 *
 * This function processes the login form data and attempts to authenticate the user.
 * The flow is:
 * 1. Parse and validate form data using the login schema
 * 2. Return validation errors if the data is invalid
 * 3. Attempt to sign in with Supabase using email/password
 * 4. Return authentication errors if sign-in fails
 * 5. Redirect to home page with auth cookies if successful
 *
 * @param request - The form submission request
 * @returns Validation errors, auth errors, or redirect on success
 */
export async function action({ request }: Route.ActionArgs) {
  // Parse form data from the request
  const formData = await request.formData();
  const intent = formData.get("intent");
  if (intent === "getRecentTournamentsBySportsType") {
    const sportsType = formData.get("sportsType");
    const tournamentList = await tournamentApi.getRecentTournaments(sportsType as string, request);
    return { tournamentList };
  } else {
    const {
      data: validData,
      success,
      error,
    } = loginSchema.safeParse(Object.fromEntries(formData));

    // Return validation errors if form data is invalid
    if (!success) {
      console.log(error.flatten().fieldErrors);
      return data({ fieldErrors: error.flatten().fieldErrors }, { status: 400 });
    }

    try {
      // Create Supabase client with request cookies for authentication
      const loginResponse = await authApi.loginTab(
        validData.tnmtId,
        validData.password,
      );
      if (loginResponse.status === 0 && loginResponse.accessToken) {
        // 토큰과 유저 정보를 클라이언트로 전달
        return {
          token: loginResponse.accessToken,
          tnmtId: validData.tnmtId,
          sportsType: loginResponse.sportsType,
          tnmtCategory: loginResponse.tnmtCategory,
          ...(loginResponse.tnmtName && { tnmtName: loginResponse.tnmtName }),
        };
      } else {
        return { error: "이메일 또는 비밀번호가 올바르지 않습니다." };
      }
    } catch (error) {
      console.log(error);
      return { error: "이메일 또는 비밀번호가 올바르지 않습니다." };
    }
  }
}

// Return error if authentication fails
// Redirect to home page with authentication cookies in headers

/**
 * Login Component
 *
 * This component renders the login form and handles user interactions.
 * It includes:
 * - Email and password input fields with validation
 * - Error display for form validation and authentication errors
 * - Password reset link
 * - Email verification resend functionality
 * - Social login options
 * - Sign up link for new users
 *
 * @param actionData - Data returned from the form action, including any errors
 */
export default function Login({ actionData, loaderData }: Route.ComponentProps) {
  const [showInput, setShowInput] = useState(false);
  const [tournamentId, setTournamentId] = useState("");
  const [sports, setSports] = useState("");
  const formRef = useRef<HTMLFormElement>(null);
  const [searchParams] = useSearchParams();

  // fetcher 추가
  const fetcher = useFetcher();
  // fetcher의 tournamentList가 있으면 사용, 없으면 loaderData 사용
  const tournamentList = useMemo<{ tnmtId: number; tnmtName: string }[]>(
    () => {
      let list =
        (fetcher.data?.tournamentList as { tnmtId: number; tnmtName: string }[]) ||
        (loaderData.recentTournaments.tnmtList as { tnmtId: number; tnmtName: string }[]) ||
        [];
      if (!Array.isArray(list)) {
        // 객체라면 tnmtList 프로퍼티를 사용
        if (list && typeof list === 'object' && 'tnmtList' in list) {
          list = (list as any).tnmtList;
        }
      }
      return Array.isArray(list) ? list : [];
    },
    [fetcher.data, loaderData.recentTournaments.tnmtList]
  );

  // 셀렉트에서 선택 시
  const handleSelectChange = (value: string) => {
    setTournamentId(value);
  };

  // 종목 선택 시 fetcher로 대회 목록 요청
  const handleSelectSports = (value: string) => {
    setSports(value);
    fetcher.submit(
      { intent: "getRecentTournamentsBySportsType", sportsType: value },
      { method: "post" }
    );
    setTournamentId(""); // 종목 바뀌면 선택 초기화
  };

  // 인풋에서 입력 시
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setTournamentId(e.target.value);
  };

  // 체크박스 토글
  const handleCheckboxChange = (checked: boolean) => {
    setShowInput(checked);
    setTournamentId(""); // 상태 초기화
  };

  // URL 파라미터로 로그아웃 처리
  useEffect(() => {
    const logoutParam = searchParams.get("logout");
    if (logoutParam === "true") {
      clearAuthData();
      // URL에서 logout 파라미터 제거
      const newUrl = new URL(window.location.href);
      newUrl.searchParams.delete("logout");
      window.history.replaceState({}, "", newUrl.toString());
    }
  }, [searchParams]);

  useEffect(() => {
    if (actionData && "token" in actionData && actionData.token) {
      localStorage.setItem("accessToken", actionData.token);
      localStorage.setItem("sportsType", actionData.sportsType);
      localStorage.setItem("tnmtCategory", actionData.tnmtCategory);
      // tnmtId는 무조건 저장 (tournamentList에서 찾지 못해도 actionData.tnmtId는 있음)
      localStorage.setItem("tnmtId", actionData.tnmtId);
      setAccessTokenToCookie(actionData.token);

      // actionData에 tnmtName이 있으면 우선 사용, 없으면 tournamentList에서 찾기
      if (actionData.tnmtName) {
        localStorage.setItem("tnmtName", actionData.tnmtName);
      } else {
        const tnmt = tournamentList.find(
          (t) => t.tnmtId.toString() === actionData.tnmtId
        );
        if (tnmt) {
          localStorage.setItem("tnmtName", tnmt.tnmtName);
        }
      }

      // 리다이렉트는 actionData.tnmtId를 사용
      window.location.href = `/tournament/${actionData.tnmtId}/gyms`;
    }
  }, [actionData]);

  const handleLogout = (e: React.MouseEvent) => {
    e.preventDefault();
    clearAuthData();
    window.location.href = `/`;
  };

  return (
    <div className="flex flex-col items-center justify-center gap-4">
      <Card className="w-full max-w-md">
        <CardHeader className="flex flex-col items-center">
          <CardTitle className="text-2xl font-semibold">
            Sign into your account
          </CardTitle>
          <CardDescription className="text-base">
            Please enter your details
          </CardDescription>
          {typeof window !== "undefined" && window.location.hostname === "localhost" && (
            <p className="text-xs text-muted-foreground mt-1 break-all">
              API: {import.meta.env.VITE_WEB_SERVICE_URL || "not set"}
            </p>
          )}
        </CardHeader>
        <CardContent className="grid gap-4">
          <Form
            className="flex w-full flex-col gap-5"
            method="post"
            ref={formRef}
          >
            {!showInput ? (
              <div className="flex flex-col items-start space-y-2">
                <div className="w-full space-y-2">
                  <Select value={sports} onValueChange={handleSelectSports}>
                    <SelectTrigger className="w-full">
                      {sports === "BT" ? "배드민턴" : sports === "SQS" ? "스쿼시" : "종목 선택"}
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="BT">배드민턴</SelectItem>
                      <SelectItem value="SQS">스쿼시</SelectItem>
                    </SelectContent>
                  </Select>
                  <Select value={tournamentId} onValueChange={handleSelectChange}>
                    <SelectTrigger className="w-full">
                      {tournamentId
                        ? tournamentList.find(
                          (t) => t.tnmtId.toString() === tournamentId
                        )?.tnmtName
                        : "최근 대회"}
                    </SelectTrigger>
                    <SelectContent>
                      {tournamentList.map((tournament) => (
                        <SelectItem
                          key={tournament.tnmtId}
                          value={tournament.tnmtId.toString()}
                        >
                          {tournament.tnmtName}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            ) : (
              <div className="flex flex-col items-start space-y-2">
                <Label htmlFor="tournamentId" className="flex flex-col items-start gap-1">
                  Tournament ID
                </Label>
                <Input
                  id="tournamentId"
                  name="tnmtId"
                  required
                  type="text"
                  placeholder="i.e 12345"
                  value={tournamentId}
                  onChange={handleInputChange}
                />
                <span className="text-sm text-muted-foreground"> 대회 아이디는 관리자 로그인 후 대회 운영 목록, 스코어보드 로그인 설정에서 확인할 수 있습니다.</span>
                {actionData && "fieldErrors" in actionData && actionData.fieldErrors.tnmtId ? (
                  <span className="text-sm text-red-500"> {actionData.fieldErrors.tnmtId}</span>
                ) : null}
              </div>

            )}
            <div className="flex flex-row items-center space-x-2">
              <Checkbox
                id="recentTournaments"
                checked={showInput}
                onCheckedChange={handleCheckboxChange}
              />
              <Label htmlFor="recentTournaments">
                찾으시는 대회가 안보이시나요?
              </Label>
            </div>
            {!showInput && <span className="text-sm text-muted-foreground">
              최근 대회는 대회 당일을 기준으로 전후 7일에 대회만 보입니다. <br />
              로그인할 대회가 보이지 않으시면, 체크하시고 대회 아이디를 직접 입력해주세요.
            </span>}
            <input type="hidden" name="tnmtId" value={tournamentId} />

            <div className="flex flex-col items-start space-y-2">
              <div className="flex w-full items-center justify-between">
                <Label
                  htmlFor="password"
                  className="flex flex-col items-start gap-1"
                >
                  Password
                </Label>
              </div>
              <Input
                id="password"
                name="password"
                required
                type="password"
                placeholder="Enter your password"
              />

              {actionData &&
                "fieldErrors" in actionData &&
                actionData.fieldErrors.password ? (
                <FormErrors errors={actionData.fieldErrors.password} />
              ) : null}
            </div>
            <FormButton label="Log in" className="w-full" />
            {actionData && "error" in actionData ? (
              <Alert variant="destructive" className="bg-destructive/10">
                <AlertCircle className="h-4 w-4" />
                <AlertTitle>로그인 실패</AlertTitle>
                <AlertDescription>{actionData.error}</AlertDescription>
              </Alert>
            ) : null}
          </Form>
        </CardContent>
      </Card>
    </div>
  );
}
